import type { SimBrotherhoodInvite, SimMyBrotherhood } from '@cruz/shared';
import { useEffect, useState } from 'react';
import { navigate } from '../challenge/router.ts';
import { simApi, SimApiError, simErrorMessage } from './api.ts';
import { brotherhoodName, num, plural, upperFirst } from './format.ts';
import { standings } from './Rivals.tsx';
import { Sheet } from './Sheet.tsx';
import { card, Notice, PageTitle, primaryButton, secondaryButton, Working } from './ui.tsx';

/*
 * «Únete a mi hermandad»: friends play their own careers in the same brotherhood. Here,
 * who plays in yours and the invitation, and the page friends open from it.
 */

const nameOf = (nickname: string | null, number: number) => nickname ?? `Hermano nº ${num(number)}`;

type Named = { name: string; popular_name: string | null };

/** "Me he hecho hermano de la Paciencia…" */
const inviteText = (b: Named, url: string) =>
  `Me he hecho hermano de ${b.popular_name ?? `la ${b.name}`} en «De cirio a hermano mayor», un juego cofrade. Inscríbete y hacemos la carrera juntos: ${url}`;

/** In the brotherhood tab: who plays in it, and inviting friends. */
export function BrothersSection({ brotherhood }: { brotherhood: Named }) {
  const [mine, setMine] = useState<SimMyBrotherhood | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  useEffect(() => {
    let alive = true;
    simApi
      .brotherhood()
      .then((m) => alive && setMine(m))
      .catch((err: unknown) => alive && setError(simErrorMessage(err)));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section aria-labelledby="brothers" className="space-y-3">
      <h2
        id="brothers"
        className="font-serif text-2xl font-semibold text-purple dark:text-gold-300"
      >
        Hermanos que juegan
      </h2>
      {error && <Notice tone="error">{error}</Notice>}
      {!mine && !error && <Working label="Pasando lista…" />}
      {mine && (
        <>
          {mine.brothers.length <= 1 ? (
            <p className="text-[1.05rem]">
              De momento juegas solo. Invita a tus amigos: se inscriben en tu hermandad con los
              números siguientes de la nómina y cada uno hace su carrera.
            </p>
          ) : (
            <ul className={`${card} divide-y divide-purple/10 px-4 dark:divide-cream/10`}>
              {standings(mine.brothers).map((b, i) => (
                <li key={b.member_number} className="flex items-start justify-between gap-3 py-2.5">
                  <div>
                    <p className="font-semibold">
                      <span className="mr-2 font-serif text-gold-700 tabular dark:text-gold-300">
                        {i + 1}.º
                      </span>
                      {b.you ? 'Tú' : nameOf(b.nickname, b.member_number)}
                      {b.founder && (
                        <span className="ml-2 rounded-full border border-gold/70 px-2 py-0.5 text-xs font-semibold text-gold-700 dark:text-gold-300">
                          Abrió la hermandad
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-ink-soft dark:text-cream/75">
                      {b.finished ? 'Carrera terminada' : b.rank_label} ·{' '}
                      {plural(b.seniority_years, 'año', 'años')}
                      {b.played_today && !b.you && ' · Hoy ha jugado'}
                    </p>
                  </div>
                  <p className="text-sm whitespace-nowrap text-ink-soft tabular dark:text-cream/75">
                    nº {num(b.member_number)}
                  </p>
                </li>
              ))}
            </ul>
          )}
          {mine.brothers.length < mine.max && (
            <button type="button" className={primaryButton} onClick={() => setInviting(true)}>
              Invitar a mis amigos
            </button>
          )}
          {mine.brothers.length > 1 && (
            <p className="text-sm text-ink-soft dark:text-cream/70">
              Cada uno vive su propia carrera en la hermandad, a su ritmo. Caben {mine.max}.
            </p>
          )}
        </>
      )}
      {inviting && (
        <InviteSheet
          brotherhood={brotherhood}
          onInvite={setMine}
          onClose={() => setInviting(false)}
        />
      )}
    </section>
  );
}

function InviteSheet({
  brotherhood,
  onInvite,
  onClose,
}: {
  brotherhood: Named;
  onInvite: (m: SimMyBrotherhood) => void;
  onClose: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [needsAccount, setNeedsAccount] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    let alive = true;
    simApi
      .invite()
      .then((m) => {
        if (!alive) return;
        onInvite(m);
        setUrl(m.url);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (err instanceof SimApiError && err.code === 'register_required') setNeedsAccount(true);
        else setError(simErrorMessage(err));
      });
    return () => {
      alive = false;
    };
  }, [onInvite]);
  const text = url ? inviteText(brotherhood, url) : '';
  const canShare = typeof navigator.share === 'function';
  return (
    <Sheet title="Invita a tus amigos" onClose={onClose}>
      {!url && !error && !needsAccount && <Working label="Preparando la invitación…" />}
      {needsAccount && (
        <div className="space-y-3">
          <p className="text-lg">
            Para invitar a tus amigos, guarda antes tu carrera: la invitación lleva tu apodo.
          </p>
          <button
            type="button"
            className={primaryButton}
            onClick={() => {
              onClose();
              navigate('/simulador/registro');
            }}
          >
            Guardar mi carrera
          </button>
        </div>
      )}
      {error && <Notice tone="error">{error}</Notice>}
      {url && (
        <div className="space-y-3">
          <p className="text-[1.05rem]">
            Quien entre con este enlace se inscribe en tu hermandad, con el número siguiente de la
            nómina, y empieza su carrera de cirio. Sin registrarse.
          </p>
          {canShare && (
            <button
              type="button"
              className={primaryButton}
              onClick={() => void navigator.share({ text, url }).catch(() => undefined)}
            >
              Compartir la invitación
            </button>
          )}
          <a
            className={canShare ? secondaryButton : primaryButton}
            href={`https://wa.me/?text=${encodeURIComponent(text)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Enviar por WhatsApp
          </a>
          <button
            type="button"
            className={secondaryButton}
            onClick={() =>
              void navigator.clipboard
                .writeText(url)
                .then(() => setCopied(true))
                .catch(() => setCopied(false))
            }
          >
            {copied ? '¡Enlace copiado!' : 'Copiar el enlace'}
          </button>
        </div>
      )}
    </Sheet>
  );
}

/** /simulador/hermandad/:code — what a friend sees when they open an invitation. */
export function JoinPage({
  code,
  onJoin,
}: {
  code: string;
  /** Joins (as a guest if need be) and opens the new career */
  onJoin: (code: string) => Promise<void>;
}) {
  const [invite, setInvite] = useState<SimBrotherhoodInvite | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  useEffect(() => {
    simApi
      .invitation(code)
      .then(setInvite)
      .catch((err: unknown) => setError(simErrorMessage(err)));
  }, [code]);
  if (error) {
    return (
      <div className="space-y-4">
        <Notice tone="error">Esta invitación ya no existe.</Notice>
        <button type="button" className={primaryButton} onClick={() => navigate('/simulador')}>
          Conocer el juego
        </button>
      </div>
    );
  }
  if (!invite) return <Working label="Buscando la hermandad…" />;
  const join = () => {
    setBusy(true);
    setJoinError(null);
    onJoin(code)
      .catch((err: unknown) => setJoinError(simErrorMessage(err)))
      .finally(() => setBusy(false));
  };
  const popular = upperFirst(brotherhoodName(invite));
  return (
    <div className="space-y-5">
      <PageTitle kicker={invite.founder ? `${invite.founder} te invita` : 'Te invitan'}>
        {popular}
      </PageTitle>
      <p className="text-lg">
        {invite.name}. Sale el {invite.day} desde {invite.church}.
      </p>
      <div className={`${card} space-y-2 px-5 py-4`}>
        <p className="font-semibold">
          {plural(invite.brothers.length, 'amigo juega', 'amigos juegan')} ya en ella:
        </p>
        <ul className="flex flex-wrap gap-2">
          {invite.brothers.map((b, i) => (
            <li
              key={i}
              className="rounded-full border border-purple/20 px-3 py-1 text-sm dark:border-cream/20"
            >
              {b.nickname ?? 'Un hermano nuevo'} · {b.rank_label}
            </li>
          ))}
        </ul>
      </div>
      {invite.member ? (
        <>
          <Notice>Ya eres hermano de esta hermandad.</Notice>
          <button type="button" className={primaryButton} onClick={() => navigate('/simulador')}>
            Ir a mi carrera
          </button>
        </>
      ) : invite.busy ? (
        <>
          <Notice>
            Ya llevas una carrera en otra hermandad. De momento, una carrera a la vez: cuando la
            termines podrás inscribirte en esta.
          </Notice>
          <button type="button" className={secondaryButton} onClick={() => navigate('/simulador')}>
            Volver a mi carrera
          </button>
        </>
      ) : invite.full ? (
        <Notice>Esta hermandad ya está completa.</Notice>
      ) : (
        <div className="space-y-3">
          <p className="text-[1.05rem]">
            Te inscribes con el número siguiente de la nómina y empiezas de cirio, en el primer
            tramo. Cada uno hace su carrera, a su ritmo, y veis cómo le va a los demás.
          </p>
          <button type="button" className={primaryButton} disabled={busy} onClick={join}>
            {busy ? 'Apuntándote en la nómina…' : `Inscribirme en ${popular}`}
          </button>
          <p className="text-center text-sm text-ink-soft dark:text-cream/70">
            Sin registrarte. Al inscribirte aceptas las{' '}
            <a href="/simulador/condiciones" className="underline">
              condiciones
            </a>{' '}
            y la{' '}
            <a href="/simulador/privacidad" className="underline">
              privacidad
            </a>
            .
          </p>
          {joinError && <Notice tone="error">{joinError}</Notice>}
        </div>
      )}
    </div>
  );
}
